import { type ReactElement, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@optimization/ssi-common';
import { type VehicleCycledEnergyRecord } from '../services/battery-monitoring-api/types/battery-monitoring-api';
import { NotAvailable } from '../NotAvailable';
import Specification from './Specification';
import MetricsGrid from './MetricsGrid';
import CycledEnergyChart from './CycledEnergyChart';
import BatteryLifeForecastChart from './BatteryLifeForecastChart';
import AvgChargePowerChart from './AvgChargePowerChart';
import ChartContentSwitcher, { type VehicleDetailView } from './ChartContentSwitcher';
import styles from './style.module.scss';
import ErrorBoundary from '@/common/components/ErrorBoundary';
import Spinner from '@/common/components/Spinner';
import { getChargingPowerChartsQueryOptions } from '@/features/BatteryMonitoring/services/battery-monitoring-api/utils/get-charging-power-charts-query-options';
import { getCycledEnergyMeasurementsQueryOptions } from '@/features/BatteryMonitoring/services/battery-monitoring-api/utils/get-cycled-energy-measurements-query-options';

interface VehicleDetailProperties {
  readonly selectedVehicle: VehicleCycledEnergyRecord;
  readonly closeVehicleDetail: () => void;
}

const VehicleDetail = ({ selectedVehicle, closeVehicleDetail }: VehicleDetailProperties): ReactElement => {
  const [selectedView, setSelectedView] = useState<VehicleDetailView>('avgCycledEnergy');
  const isPhev = selectedVehicle.propulsion === 'PHEV';
  const {
    isLoading: isMeasurementsLoading,
    isError: isMeasurementsError,
    data: measurementsData,
  } = useQuery({
    ...getCycledEnergyMeasurementsQueryOptions({ chassisNumber: selectedVehicle.chassisNumber }),
    enabled: selectedView === 'avgCycledEnergy' && !isPhev,
  });
  const {
    isLoading: isChargePowerLoading,
    isError: isChargePowerError,
    data: chargePowerData,
  } = useQuery({
    ...getChargingPowerChartsQueryOptions({ chassisNumber: selectedVehicle.chassisNumber }),
    enabled: selectedView === 'avgChargePower',
  });

  const renderChart = (): ReactElement => {
    if (isPhev && selectedView !== 'avgChargePower') {
      return (
        <div className={styles['chart-loading']}>
          <NotAvailable label="Not available" />
        </div>
      );
    }
    if (selectedView === 'batteryLifeForecast') {
      return (
        <BatteryLifeForecastChart
          currentSoh={selectedVehicle.stateOfHealthRatio}
          currentAgeMonths={selectedVehicle.calendarAgeMonths}
          estimatedSoh8Years={selectedVehicle.stateOfHealthRatioAfter8Years}
          estimatedSoh12Years={selectedVehicle.stateOfHealthRatioAfter12Years}
          estimatedSoh15Years={selectedVehicle.stateOfHealthRatioAfter15Years}
          estimatedTotalYears={selectedVehicle.estimatedTotalYears}
        />
      );
    }

    if (selectedView === 'avgChargePower') {
      if (isChargePowerLoading) {
        return (
          <div className={styles['chart-loading']}>
            <Spinner size="md" />
          </div>
        );
      }

      if (isChargePowerError) {
        return (
          <div className={styles['chart-error']} role="alert">
            Unable to load charge power data.
          </div>
        );
      }

      return <AvgChargePowerChart chargePower={chargePowerData?.chargePower} />;
    }

    if (isMeasurementsLoading) {
      return (
        <div className={styles['chart-loading']}>
          <Spinner size="md" />
        </div>
      );
    }

    if (isMeasurementsError) {
      return (
        <div className={styles['chart-error']} role="alert">
          Unable to load cycled energy data.
        </div>
      );
    }

    return <CycledEnergyChart records={measurementsData?.records ?? []} />;
  };

  return (
    <Modal
      size="lg"
      header={`Chassis - ${selectedVehicle.chassisNumber}`}
      variant="primary"
      onClose={closeVehicleDetail}
    >
      <div className={styles.container}>
        <div className={styles['top-section']}>
          <ErrorBoundary>
            <Specification selectedVehicle={selectedVehicle} />
          </ErrorBoundary>
          <ErrorBoundary>
            <MetricsGrid selectedVehicle={selectedVehicle} />
          </ErrorBoundary>
        </div>
        <ErrorBoundary>
          <div className={styles['chart-section']}>
            <ChartContentSwitcher
              selectedView={selectedView}
              onViewChange={setSelectedView}
              className={styles['inline-tabs']}
            />
            {renderChart()}
          </div>
        </ErrorBoundary>
      </div>
    </Modal>
  );
};

export default VehicleDetail;
